import React, {
  ReactElement,
  useState,
  useCallback,
  useMemo,
  useRef, useEffect,
} from 'react';
import Dialog from '@mui/material/Dialog';
import api from '../../../services/api';
import {IconButton, Stack, StepLabel} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {TransitionProps} from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import {v4 as uuidv4} from 'uuid';
import Paper, {PaperProps} from '@mui/material/Paper';
import Draggable from 'react-draggable';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import {ChevronLeft, ChevronRight, Send} from '@mui/icons-material';
import {toaster} from '../../../services/toaster';
import {ServiceProviderPicker} from '../../../pages/General/ReservationCheck/ServiceProviderPicker';
import {AddNewActivityReservationSchema} from './AddNewActivityReservationSchema';
import {State} from './State';
import {ServicesSchema} from "./ServicesSchema";
import {PricesSchema} from "./PricesSchema";
import toast from "react-hot-toast";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const AddNewActivityReservationWithStepper = ({
                                                       onClose,
                                                       onSubmit,
                                                       data,
                                                     }: {
  onClose: () => void;
  onSubmit: () => void;
  data: any;
}): ReactElement => {
  const [formState, setFormState] = useState<any>({
    group: data
  });
  const slidingRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const fetchServiceDetails = useCallback(() => {
    const current = slidingRef.current?.getState();

    setFormState((prev: any) => ({
      ...prev,
      ...(current ?? {}),
    }));

    setActiveStepIndex(activeStepIndex + 1);

  }, [slidingRef]);

  const fetchPriceDetails = useCallback(() => {
    setActiveStepIndex(activeStepIndex + 1);
  }, [slidingRef, formState]);

  const submitActivityReservationCreate = useCallback(async () => {

    setLoading(true);

    await api.post(`/activity-reservations/create/quick`, formState).then(() => {
      toast.success('The activity was created successfully');
      onSubmit();
    })
      .catch(() => {
          toast.error('Creation failed. Please try again later');
      })
      .finally(() => {
        setLoading(false);
      });

  }, [formState]);


  const steps = [
    {
      label: 'Add new Activity',
      component: AddNewActivityReservationSchema,
      forwardButtonIcon: loading ? (
        <CircularProgress color="inherit" size={'1em'}/>
      ) : undefined,
      forwardButtonAction: () => {
        fetchServiceDetails();
      },
      forwardButtonDisabled: loading,
    },
    {
      label: (formState?.group && formState?.activity) ? `${formState?.group?.name} - ${formState?.activity?.name}` : 'Select services',
      component: ServicesSchema,
      forwardButtonDisabled: loading,
      forwardButtonIcon: loading ? (
        <CircularProgress color="inherit" size={'1em'}/>
      ) : undefined,
      forwardButtonAction: () => {
        fetchPriceDetails();
      },
    },
    {
      label: 'Finalize Prices',
      component: PricesSchema,
      forwardButtonDisabled: loading,
      forwardButtonIcon: loading ? (
        <CircularProgress color="inherit" size={'1em'}/>
      ) : (
        <Send/>
      ),
      forwardButtonLabel: 'Finish',
      forwardButtonAction: () => {
        submitActivityReservationCreate();
      },
    },
  ];

  const {
    component: ActiveStepComponent,
    forwardButtonIcon,
    forwardButtonDisabled,
    forwardButtonLabel,
    forwardButtonAction,
    label,
  } = steps[activeStepIndex];

  const draggablePaneId = useMemo(() => {
    return `draggable-responsive-add-new-dialog-${uuidv4()}`;
  }, []);

  const PaperComponent = useCallback((props: PaperProps) => {
    return (
      <Draggable
        handle={`#${draggablePaneId}, .drag-handle`}
        cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
      >
        <Paper {...props} />
      </Draggable>
    );
  }, []);

  return (
    <>
      <Dialog
        PaperComponent={PaperComponent}
        fullScreen
        open={true}
        onClose={onClose}
        TransitionComponent={Transition}
        sx={{
          '& .MuiPaper-root.MuiDialog-paper': {
            maxWidth: 'calc(100% - 180px)',
            height: 'calc(100% - 100px)',
            borderRadius: '10px'
          },
        }}
      >
        <AppBar
          sx={{
            position: 'relative',
            background: '#256861',
          }}
        >
          <Toolbar id={draggablePaneId}>
            <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
              {`${formState?.group?.name?(`${formState?.group?.name} - `):''} ${formState?.activity?.name?(`${formState?.activity?.name} - `):''} Add New - Activity Reservation`}
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon/>
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            p: '20px',
            height: '100%',
          }}
        >
          <Stepper activeStep={activeStepIndex}>
            {steps.map(({label}) => (
              <Step key={label}>
                <StepLabel
                sx={{
                  fontSize: '13px'
                }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              background: '#efefef',
              borderRadius: '10px',
              p: '10px',
              mt: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent'
              }
            }}
          >
            <Box sx={{alignSelf: 'center'}}>
              <ActiveStepComponent
                formState={formState}
                setFormState={setFormState}
                slidingRef={slidingRef}
              />
            </Box>
            <Stack
              direction="row"
              sx={{justifyContent: 'space-between', mt: '10px'}}
            >
              <Button
                onClick={() =>
                  activeStepIndex > 0 && setActiveStepIndex(activeStepIndex - 1)
                }
                variant="outlined"
                startIcon={<ChevronLeft/>}
                disabled={activeStepIndex === 0}
              >
                Back
              </Button>
              <Button
                onClick={
                  forwardButtonAction ||
                  (() => setActiveStepIndex(activeStepIndex + 1))
                }
                variant="contained"
                endIcon={forwardButtonIcon || <ChevronRight/>}
                disabled={forwardButtonDisabled}
              >
                {forwardButtonLabel || 'Next'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
